import {createSelector} from 'reselect';

import {Controller} from '@stubhub/react-store-provider';

import {getEntity, pluralize} from '../../utils/catalog';

const seoLinksSelectorFactory = () =>
  createSelector(
    (localState) => localState.seoLinks,
    (seoLinks = []) => seoLinks,
  );
const seoLinksNumFoundSelectorFactory = () =>
  createSelector(
    (localState) => localState.numFound,
    (numFound = 0) => numFound,
  );
const instanceIdSelectorFactory = () =>
  createSelector(
    (localState) => localState.instanceId,
    (instanceId) => instanceId,
  );

function controllerFactory(namespace) {
  const NAMESPACE = `${namespace}.seo.links`;

  const seoLinksSelector = seoLinksSelectorFactory();
  const numFoundSelector = seoLinksNumFoundSelectorFactory();
  const instanceIdSelector = instanceIdSelectorFactory();

  const controller = new Controller({
    namespace: NAMESPACE,

    mapStateToProps(state) {
      const localState = this.getLocalState(state);

      return {
        seoLinks: seoLinksSelector(localState),
        numFound: numFoundSelector(localState),
        loading: !!localState.loading,
        instanceId: instanceIdSelector(localState),
      };
    },

    actionCreators: {
      loadEntitySEOLinks,
      resetChildSEOLinks,
    },

    reducers: [`${namespace}:ENTITY_SEO_LINKS_LOADING`, `${namespace}:ENTITY_SEO_LINKS_LOADED`],
  });

  /**
   * Method to Reset the the SEO Links component State.
   *
   */
  function resetChildSEOLinks() {
    return (dispatch) => {
      dispatch({type: `${namespace}:ENTITY_SEO_LINKS_RESET`});
    };
  }

  /**
   * Method to Load the SEO Links from the Search Catalog API
   * @param entityType {string}
   * @param entityId {string}
   * @param reset {boolean}
   *
   */
  function loadEntitySEOLinks({entityType, entityId, resultsPerPage, currentPage, showPagination, apiParams = {}}) {
    return (dispatch, getState) => {
      /* istanbul ignore next */
      const state = getState() || {};
      const {gsConfig = {}} = state;

      const defaultAPIParams = {
        start: 0,
        rows: resultsPerPage,
        status: 'active',
        sort: 'name asc',
        fieldList: 'name,id,url',
      };

      /**
       * Set Start and Rows if Pagination is enabled.
       *
       */
      if (showPagination) {
        defaultAPIParams.start = currentPage * resultsPerPage - resultsPerPage;
        defaultAPIParams.rows = resultsPerPage;
      }

      /**
       * Merge the Default Params with Externally Passed Params
       *
       */
      apiParams = {...defaultAPIParams, ...apiParams};

      /**
       * Notify the Component that its loading the Entities
       *
       */
      dispatch({type: `${namespace}:ENTITY_SEO_LINKS_LOADING`, loading: true, seoLinks: [], numFound: 0});

      /**
       * Use the Catalog Utilities Method to fetch the Seolinks from the API.
       *
       */
      return getEntity(entityType, '', gsConfig.shstoreId, getState().lang, true, apiParams).then((body = {}) => {
        /**
         * @TODO - `REMOVE_useSearchCatalogAPI` for pluralize is deprecated should be removed when the Search API adds mapping for `ENTITY_TYPE_GEO`
         *
         */
        const {[pluralize(entityType, true)]: seoLinks, numFound} = body;
        const instanceId = `${entityType}-${entityId}`;
        dispatch({type: `${namespace}:ENTITY_SEO_LINKS_LOADED`, loading: false, seoLinks, numFound, instanceId});

        return null;
      });
    };
  }

  controller.addResetReducer(`${namespace}:ENTITY_SEO_LINKS_RESET`);

  return controller;
}

export default controllerFactory;
